import { useEffect, useState } from "react";
import { Badge, Button, Card, CardBody, Col, Row } from "reactstrap";
import WizardPanel from "../WizardPanel";
import { PanelComboField, PanelField } from "../panelFields";
import { AsyncButton } from "../../../common/buttons";
import { getRecorderBrands } from "../../../cameraPrototypes/shared/captureDeviceService";
import sanitizePort from "../shared/sanitizePort";
import { captureWizardError } from "../shared/wizardObservability";

const dvrKey = (dvr) => dvr?.id ?? dvr?.tempId;

// Matches VideoRecorderType.name on the backend; a longer brand can't be projected to a recorder type.
const BRAND_MAX_LENGTH = 32;

function PortField({ label, value, onChange, placeholder }) {
  return (
    <PanelField
      label={label}
      type="number"
      min={1}
      max={65535}
      step={1}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  );
}

function StatusBadge({ status }) {
  if (status === "configured") {
    return (
      <Badge color="success" pill>
        Configured
      </Badge>
    );
  }
  return (
    <Badge color="secondary" pill>
      Incomplete
    </Badge>
  );
}

// One DVR's fields. Draft → Create (POST); persisted → autosave (PATCH).
function DvrCard({ dvr, index, isCloud, brandOptions, alreadyApplied, onField, onCreate, onRemove }) {
  const key = dvrKey(dvr);
  const isDraft = dvr?.id == null;
  const setField = (field) => (value) => onField(key, field, value);
  const handlePortChange = (field) => (value) => onField(key, field, sanitizePort(value));
  const canCreate = Boolean(dvr?.dvr_login && dvr?.dvr_password && (isCloud || dvr?.dvr_ipv4));

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3" style={{ gap: "0.75rem" }}>
          <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
            <h6 className="mb-0">DVR {index + 1}</h6>
            {!isDraft && <StatusBadge status={dvr?.status} />}
            {isDraft && (
              <Badge color="warning" pill>
                Not created yet
              </Badge>
            )}
          </div>
          <AsyncButton color="link" className="text-danger p-0" disabled={alreadyApplied} onClick={() => onRemove(key)}>
            <i className="fas fa-trash mr-1" />
            Remove
          </AsyncButton>
        </div>

        <Row>
          <Col md={6}>
            <PanelComboField
              label="Brand"
              placeholder="e.g. Hikvision, Dahua"
              value={dvr?.dvr_brand}
              options={brandOptions}
              maxLength={BRAND_MAX_LENGTH}
              onChange={setField("dvr_brand")}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <PanelField
              label="Login"
              placeholder="admin"
              autoComplete="off"
              value={dvr?.dvr_login}
              onChange={setField("dvr_login")}
            />
          </Col>
          <Col md={6}>
            <PanelField
              label="Password"
              placeholder="DVR account password"
              autoComplete="new-password"
              value={dvr?.dvr_password}
              onChange={setField("dvr_password")}
            />
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <PanelField
              label="MAC address"
              placeholder="e.g. 00:1A:2B:3C:4D:5E"
              mono
              value={dvr?.dvr_mac_address}
              onChange={setField("dvr_mac_address")}
            />
          </Col>
          <Col md={6}>
            <PanelField
              label="LAN IP"
              placeholder="e.g. 192.168.1.105"
              mono
              value={dvr?.dvr_ipv4}
              onChange={setField("dvr_ipv4")}
            />
          </Col>
        </Row>

        {isCloud ? (
          <>
            <Row>
              <Col md={6}>
                <PortField
                  label="RTSP port (internal)"
                  value={dvr?.dvr_rtsp_port}
                  onChange={handlePortChange("dvr_rtsp_port")}
                />
              </Col>
              <Col md={6}>
                <PortField
                  label="HTTP port (internal)"
                  value={dvr?.dvr_http_port}
                  onChange={handlePortChange("dvr_http_port")}
                />
              </Col>
            </Row>
            {/* External ports are backend-assigned on create; shown afterwards as editable overrides. */}
            {!isDraft && (
              <>
                <Row>
                  <Col md={6}>
                    <PortField
                      label="External RTSP port"
                      placeholder="Auto-assigned"
                      value={dvr?.dvr_external_rtsp_port}
                      onChange={handlePortChange("dvr_external_rtsp_port")}
                    />
                  </Col>
                  <Col md={6}>
                    <PortField
                      label="External HTTP port"
                      placeholder="Auto-assigned"
                      value={dvr?.dvr_external_http_port}
                      onChange={handlePortChange("dvr_external_http_port")}
                    />
                  </Col>
                </Row>
                <small className="text-muted">
                  External ports are assigned automatically — override only if needed.
                </small>
              </>
            )}
          </>
        ) : (
          <Row>
            <Col md={6}>
              <PortField label="RTSP port" value={dvr?.dvr_rtsp_port} onChange={handlePortChange("dvr_rtsp_port")} />
            </Col>
            <Col md={6}>
              <PortField label="HTTP port" value={dvr?.dvr_http_port} onChange={handlePortChange("dvr_http_port")} />
            </Col>
          </Row>
        )}

        {isDraft && (
          <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
            <AsyncButton color="primary" disabled={!canCreate} onClick={() => onCreate(key)}>
              <i className="fas fa-plus mr-2" />
              Create DVR
            </AsyncButton>
            {!canCreate && (
              <small className="text-muted">
                {isCloud
                  ? "Login and password are required to create a DVR."
                  : "Login, password, and IPv4 are required to create a DVR."}
              </small>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * @param {import("../WizardCard").StepPanelProps} props
 *
 * List of DVR cards with add/create/edit/delete.
 */
export default function VideoRecorderPanel({ installation, dvrStore, status, bottomNav }) {
  const { dvrs, addDraftDvr, createDvr, setDvrField, removeDvr } = dvrStore;
  const isCloud = Boolean(installation?.is_cloud);
  const alreadyApplied =
    installation?.step === "waiting_for_veesion" ||
    installation?.step === "user_creation" ||
    installation?.step === "completed";

  // Brand suggestions only — the field still accepts free text.
  const [brandOptions, setBrandOptions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getRecorderBrands()
      .then((brands) => {
        if (!cancelled) setBrandOptions((brands ?? []).map((b) => b.name).filter(Boolean));
      })
      .catch((err) =>
        captureWizardError(err, "recorderBrands", { identifier: installation?.uid ?? installation?.deal_id })
      );
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <WizardPanel
      title="Video Recorders or IP Cameras"
      subtitle="Device parameters collected on-site — add one card per recorder"
      status={status}
      bottomNav={bottomNav}
    >
      {(dvrs ?? []).length === 0 && (
        <Card className="mb-3">
          <CardBody className="text-muted">No DVRs yet — add one to get started.</CardBody>
        </Card>
      )}

      {(dvrs ?? []).map((dvr, index) => (
        <DvrCard
          key={dvrKey(dvr)}
          dvr={dvr}
          index={index}
          isCloud={isCloud}
          brandOptions={brandOptions}
          alreadyApplied={alreadyApplied}
          onField={setDvrField}
          onCreate={createDvr}
          onRemove={removeDvr}
        />
      ))}

      <Button color="secondary" outline onClick={addDraftDvr}>
        <i className="fas fa-plus mr-2" />
        Add DVR
      </Button>
    </WizardPanel>
  );
}
